// src/components/ProtectedRoute.jsx
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ROLES, STATUSES, APPROVED_ROLES, PLANS } from "../constants/roles";

/**
 * ✅ Canonical Plan Model (single source of truth)
 * plan: "trial" | "monthly" | "yearly" | "free"
 *
 * Trial state now comes from AuthContext derived fields:
 * - userDoc.isTrialActive (boolean)
 * - userDoc.isTrialExpired (boolean)
 * - userDoc.trialEndsAtMs (number | null)
 *
 * ⚠️ SECURITY: Client-side checks are for UX only!
 * Server-side enforcement happens via:
 * 1. Firestore rules (request.time checks)
 * 2. Scheduled Cloud Function (daily downgrade)
 */

function hasPaidPlan(userDoc) {
  const plan = userDoc?.plan;
  return plan === PLANS.MONTHLY || plan === PLANS.YEARLY;
}

/**
 * Decide access once. No duplicate checks elsewhere.
 */
function getAccessDecision({ userDoc, requiredRole, pathname }) {
  // 1) Status gates (apply to all roles) - ✅ Use constants
  if (userDoc?.status === STATUSES.PENDING) {
    return { allow: false, redirect: "/pending-approval" };
  }

  // ✅ REMOVED: pending_card_verification gate (no more card verification)

  if (userDoc?.status === STATUSES.DISABLED) {
    return { allow: false, hardBlock: "disabled" };
  }

  // 2) Role validity
  const role = userDoc?.role;
  if (!APPROVED_ROLES.has(role)) {
    return { allow: false, hardBlock: "invalid-role" };
  }

  // 3) Required role gate (master override)
  if (requiredRole && role !== requiredRole && role !== ROLES.MASTER) {
    return { allow: false, hardBlock: "wrong-role" };
  }

  // 4) Subscription/trial gate (teachers only)
  // ✅ NEW APPROACH: Read-Only Grace Period
  // - Expired users CAN view pages (dashboard, quizzes, students)
  // - Action-level blocking happens in components (create, edit, delete)
  // - Professional UX: banner + contextual upgrade prompts
  if (role === ROLES.TEACHER) {
    // Tester bypass stays
    if (userDoc?.isTesterAccount === true) {
      return { allow: true };
    }

    // Always allow billing / auth related pages
    const alwaysAllowed = ["/billing", "/subscription-required", "/logout", "/pending-approval"];
    const isAlwaysAllowed = alwaysAllowed.some((p) => pathname.startsWith(p));
    if (isAlwaysAllowed) return { allow: true };

    // ✅ CHANGED: Allow ALL teachers to view pages (read-only for expired)
    // Paid plans get full access (create/edit/delete)
    // Active trials get full access (create/edit/delete)
    // Expired trials get read-only access (view only, action buttons disabled)

    // No redirect to subscription-required - let them in with banner
    return { allow: true };
  }

  // students/admin allowed here (other checks handled above)
  return { allow: true };
}

export default function ProtectedRoute({ children, requiredRole }) {
  const { user, userDoc, userRole, loading, userDocError } = useAuth();
  const location = useLocation();

  console.log("🔍 ProtectedRoute State:", {
    path: location.pathname,
    loading,
    hasUser: !!user,
    userUid: user?.uid,
    hasUserDoc: !!userDoc,
    userDocError: userDocError?.code,
    userRole: userDoc?.role,
    userStatus: userDoc?.status,
    requiredRole,
    ROLES_MASTER: ROLES.MASTER,
    STATUSES_ACTIVE: STATUSES.ACTIVE,
  });

  // 0) Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Loading…</h2>
          <p className="text-gray-400">Verifying your account.</p>
        </div>
      </div>
    );
  }

  // 1) Not signed in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 2) Firestore permission error
  if (userDocError) {
    console.error("❌ ProtectedRoute: Firestore permissions error", userDocError);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="max-w-2xl p-8 bg-gray-800 rounded-lg">
          <h2 className="text-2xl font-bold mb-4 text-red-500">⚠️ Database Access Error</h2>
          <p className="mb-4 text-gray-300">
            Unable to load your account data due to a permissions error.
          </p>
          <div className="bg-gray-700 p-4 rounded mb-4">
            <p className="text-sm font-mono text-red-300">
              Error: {userDocError.code} - {userDocError.message}
            </p>
          </div>
          <p className="mb-4 text-gray-400 text-sm">
            <strong>Technical details:</strong> Firestore security rules are blocking access to users/{user?.uid}
          </p>
          <ul className="list-disc list-inside text-gray-400 text-sm mb-6">
            <li>Refreshing the page (Ctrl+Shift+R or Cmd+Shift+R)</li>
            <li>Logging out and logging back in</li>
          </ul>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition duration-200 mr-3"
          >
            🔄 Refresh Page
          </button>
          <button
            onClick={() => (window.location.href = "/logout")}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition duration-200"
          >
            🚪 Logout
          </button>
        </div>
      </div>
    );
  }

  // 3) Signed in but userDoc not ready yet (do NOT redirect)
  if (!userDoc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Loading Profile…</h2>
          <p className="text-gray-400">Getting your account details.</p>
        </div>
      </div>
    );
  }

  // 4) Master admin override - MUST HAPPEN FIRST (normalize strings for safety)
  const normalizedRole = (userDoc?.role || "").trim();
  const normalizedStatus = (userDoc?.status || "").trim();

  if (normalizedRole === ROLES.MASTER && normalizedStatus === STATUSES.ACTIVE) {
    console.log('✅ ProtectedRoute: Master admin bypass activated for', location.pathname);
    return children;
  }

  // Additional safety: Check for any variant of master/admin role
  if ((normalizedRole === "masterAdmin" || normalizedRole === "master_admin") && normalizedStatus === "active") {
    console.log('✅ ProtectedRoute: Master admin bypass activated (variant) for', location.pathname);
    return children;
  }

  // 5) Single access decision
  const decision = getAccessDecision({
    userDoc,
    requiredRole,
    pathname: location.pathname,
  });

  if (decision.allow) return children;

  if (decision.redirect) {
    return <Navigate to={decision.redirect} replace />;
  }

  // Hard blocks
  if (decision.hardBlock === "disabled") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-red-500">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Account Disabled</h2>
          <p className="mb-4">Your account has been disabled.</p>
          <p className="text-gray-400">Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (decision.hardBlock === "wrong-role") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-red-500">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p className="mb-4">{requiredRole} role required.</p>
          <p className="text-gray-400">Current role: {userDoc?.role || "None"}</p>
        </div>
      </div>
    );
  }

  // invalid-role fallback
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-red-500">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p className="mb-4">Invalid user role.</p>
        <p className="text-gray-400">Current role: {userDoc?.role || "None"}</p>
      </div>
    </div>
  );
}
